import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Ty, { TypographyTypeMap } from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { isNil } from 'lodash';

export type HeaderProps = {
  title: string;
  description?: string | React.ReactNode;
  descriptionBreakpoint?: number;
  buttonsBreakpoint?: number;
  additionalDescription?: string | React.ReactNode;
  dataTestId: string;
  maxDescriptionWidth?: string;
  variant?: TypographyTypeMap['props']['variant'];
  chip?: {
    color: 'primary' | 'default' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | undefined;
    label: string;
    icon?: React.ReactElement;
  };
  isReportHeader?: boolean;
  action?: React.ReactNode;
  headerActions?: React.ReactNode[];
  headerMarginBottom?: number;
  headerMarginTop?: number;
  alignActionsStart?: boolean;
};

function Header(props: HeaderProps) {
  const {
    title,
    description,
    descriptionBreakpoint,
    buttonsBreakpoint,
    additionalDescription,
    dataTestId,
    maxDescriptionWidth,
    variant,
    chip,
    isReportHeader,
    action,
    headerActions,
    headerMarginBottom,
    headerMarginTop,
    alignActionsStart,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <header>
      <Box
        mt={isMobile ? 1 : headerMarginTop}
        mb={isReportHeader ? 1 : headerMarginBottom}
        data-testid={dataTestId}
        display='flex'
        justifyContent='flex-start'
      >
        <Ty data-testid='header-text' variant={variant} flexGrow={1}>
          {title}
          {chip ? (
            <Chip
              icon={chip.icon}
              color={chip.color}
              label={chip.label}
              sx={{ marginLeft: theme.spacing(1), mb: '8px' }}
            />
          ) : null}
        </Ty>
        {headerActions && (
          <Grid justifyContent='flex-end' alignItems='end' gap='16px' display='flex'>
            {headerActions.map((button) => (
              <React.Fragment key={uuidv4()}>{button}</React.Fragment>
            ))}
          </Grid>
        )}
      </Box>
      {(description || additionalDescription || action) && (
        <Grid container>
          <Grid xs={descriptionBreakpoint} item>
            {!isNil(description) ? (
              <Box
                data-testid='description'
                sx={{ maxWidth: maxDescriptionWidth, color: theme.palette.text.secondary }}
                mb={2}
              >
                {description}
              </Box>
            ) : null}
            {!isNil(additionalDescription) ? (
              <Box sx={{ maxWidth: maxDescriptionWidth, color: theme.palette.text.secondary }} mb={2}>
                {additionalDescription}
              </Box>
            ) : null}
          </Grid>
          {!isNil(action) && (
            <Grid
              item
              sx={{ marginBottom: 2 }}
              display='flex'
              justifyContent='flex-end'
              alignItems={alignActionsStart ? 'flex-start' : 'flex-end'}
              xs={buttonsBreakpoint}
            >
              <Grid item> {action} </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </header>
  );
}

Header.defaultProps = {
  alignActionsStart: false,
  description: undefined,
  descriptionBreakpoint: 8,
  buttonsBreakpoint: 4,
  additionalDescription: undefined,
  maxDescriptionWidth: '70%',
  variant: 'h5',
  chip: undefined,
  isReportHeader: false,
  action: null,
  headerActions: undefined,
  headerMarginBottom: 2,
  headerMarginTop: 0,
};

export default Header;
